import {
    backfillCannedMessageTranslations,
    filterSharedCannedMessages,
    getValidSharedCannedMessageRoots,
    isCannedMsgEqual,
    isParentOf,
    normalizeCannedMessageLanguages,
    sortCannedMessages,
    sortCannedMessagesLanguages,
} from 'utils/cannedmessages'
import {
    useAppDispatch, useAppSelector,
} from 'hooks'
import {NinchatCannedMessage} from 'api/types/properties'
import {NinchatProperties} from 'api/types'
import React from 'react'
import {SearchAutocompleteItem} from 'components/SearchAutocomplete/types'
import {updateRealm} from 'api/action/updateRealm'
import {updateUser} from 'api/action/updateUser'
import {useRealm} from 'hooks/ninchat'
import {useTranslation} from 'react-i18next'

type ILanguage= Array<{id: string, name: string, content: string, code: string}>
const languageList: ILanguage = []
let loadPromise: Promise<ILanguage> | null = null

// eslint-disable-next-line require-await
const load = async (): Promise<ILanguage> => {
    if (loadPromise) {
        return loadPromise
    }
    if (languageList.length > 0) {
        return languageList
    }
    loadPromise = fetch('assets/iso_639_3.json')
        .then((res) => res.json())
        .then((data: ILanguage) => {
            languageList.push(...data)
            loadPromise = null
            return data
        })
        .catch((err) => {
            loadPromise = null
            throw err
        })

    return loadPromise
}

type CannedMessagesHook = {
    allLanguages: SearchAutocompleteItem[]
    cannedMessages: NinchatCannedMessage[]
    cannedMessagesLanguages: NinchatProperties.NinchatCannedMessageLanguages
    saveAvailable: boolean
    save: (
        updatedCannedMessages: NinchatCannedMessage[],
        cannedMessageLanguages: NinchatProperties.NinchatCannedMessageLanguages,
    ) => Promise<boolean>
    toItem: (cannedMessage: SearchAutocompleteItem) => NinchatProperties.NinchatCannedMessageLanguages[0]
    getCode: (id: string) => string
}

export type SaveCannedMsg = CannedMessagesHook['save']

export const filterCannedMessagesByQueue = (
    cannedMessages: Array<NinchatCannedMessage>,
    queueID: NinchatProperties.QueueID,
    queueSettings?: {shared_canned_messages?: string[]},
) => filterSharedCannedMessages(cannedMessages, queueID, queueSettings?.shared_canned_messages)

export const isSharedCannedMessageSelected = (
    cannedMessage: NinchatProperties.NinchatCannedMessage,
    sharedCannedMessages: string[],
): boolean => (getValidSharedCannedMessageRoots(sharedCannedMessages) ?? []).some((selectedKeyword) => {
    const selectedNode = {
        keywords: [selectedKeyword],
        message: '',
    }
    return isCannedMsgEqual(cannedMessage, selectedNode) || isParentOf(selectedNode, cannedMessage)
})

export const getLegacySharedCannedMessageRoots = (
    cannedMessages: Array<NinchatProperties.NinchatCannedMessage>,
    queueId: string,
): string[] => [...new Set(cannedMessages
    .filter((cannedMessage) => cannedMessage.allowedQueues?.includes(queueId))
    .map((cannedMessage) => cannedMessage.keywords[0]?.split('/')[0] ?? '')
    .filter(Boolean))]

export const getInitialSharedCannedMessages = (
    sharedCannedMessages: string[] | undefined,
    cannedMessages: Array<NinchatProperties.NinchatCannedMessage>,
    queueId: string,
): string[] => getValidSharedCannedMessageRoots(sharedCannedMessages) ?? getLegacySharedCannedMessageRoots(cannedMessages, queueId)

export const useCannedMessages = ({
    organization,
    realmId,
}: {
    organization?: boolean;
    realmId?: string;
    userId?: string;
}): CannedMessagesHook => {
    const {t} = useTranslation()
    const dispatch = useAppDispatch()
    const realm = useRealm(realmId, true)
    const userSettings = useAppSelector(({ninchat}) => ninchat.user_settings)

    const [iso639_3Languages, setIso639_3Languages] = React.useState <ILanguage>([])
    const [loadingLanguages, setLoadingLanguages] = React.useState(true)

    React.useEffect(() => {
        load()
            .then((data) => {
                setIso639_3Languages(data)
                setLoadingLanguages(false)
            })
    }, [])

    const allLanguages = React.useMemo(() => iso639_3Languages.map((item) => ({
        action: t('Add'),
        code: item.code,
        content: item.name,
        default: false,
        id: item.id,
        name: item.name,
    })), [iso639_3Languages.length])


    const userOrOrganizationCannedMessages = organization
        ? realm?.realm_settings?.canned_messages ?? []
        : userSettings?.canned_messages ?? []

    const cannedMessages = userOrOrganizationCannedMessages.map((msg) => ({
        ...msg,
        translations: backfillCannedMessageTranslations(msg),
    }))

    const supportedCannedMessageLanguages = organization
        ? realm?.realm_settings?.supported_canned_message_languages
        : userSettings?.supported_canned_message_languages

    // normalizeCannedMessageLanguages without useMemo respond with fresh array every time
    // each render cannedMessagesLanguages array was treated as new one, which cause unexpected behaviours when adding/removing languages
    const cannedMessagesLanguages = React.useMemo(
        () => normalizeCannedMessageLanguages(supportedCannedMessageLanguages ?? []),
        [supportedCannedMessageLanguages],
    )

    const saveAvailable = !loadingLanguages && (!organization || realm?._describe === 'fulfilled')

    const toItem = (cannedMessage: SearchAutocompleteItem): NinchatProperties.NinchatCannedMessageLanguages[0] => {
        const retval = {
            id: cannedMessage.id,
            isDefault: cannedMessage.isDefault ?? false,
            name: cannedMessage.name,
        }
        return retval
    }

    const getCode = (id: string): string => id?.split('-')?.[0] ?? ''

    const save = (
        updatedCannedMessages: NinchatCannedMessage[],
        cannedMessageLanguages: NinchatProperties.NinchatCannedMessageLanguages,
    ): Promise<boolean> => {
        if (!saveAvailable) {
            return Promise.resolve(false)
        }

        const sortedCannedMessages = sortCannedMessages(updatedCannedMessages)
        if (organization && realmId) {
            return dispatch(updateRealm({
                realm_id: realmId,
                realm_settings: {
                    canned_messages: sortedCannedMessages,
                    supported_canned_message_languages: cannedMessageLanguages,
                },
            })).unwrap()
                .then(
                    () => true,
                    () => false,
                )
        }

        return dispatch(updateUser({
            user_settings: {
                canned_messages: sortedCannedMessages,
                supported_canned_message_languages: cannedMessageLanguages,
            },
        })).unwrap()
            .then(
                () => true,
                () => false,
            )
    }

    if (loadingLanguages) {
        return {
            allLanguages: [],
            cannedMessages: [],
            cannedMessagesLanguages: [],
            getCode: () => '',
            save,
            saveAvailable,
            toItem: () => ({
                id: '',
                isDefault: false,
                name: '',
            }),
        }
    }

    return {
        allLanguages,
        cannedMessages: sortCannedMessages(cannedMessages),
        cannedMessagesLanguages: sortCannedMessagesLanguages(cannedMessagesLanguages, t),
        getCode,
        save,
        saveAvailable,
        toItem,
    }
}
